import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../../config";
import { toApiDate } from "../customerPerformanceHelpers";
import {
  getCell,
  getTotalJobs,
  getIssuePercent,
} from "./customerPerformanceWidgetHelpers";
import "./CustomerPerformanceWidget.css";

// Shared presentational core for the customer-performance widgets — per-period
// (WTD / QTD / YTD) counts of order-change events, with a computed "% of jobs"
// per row. The backend does all the eligibility/bucketing work; this just
// renders whatever result[0] it receives.
//
// The two variants differ in endpoint, title, the reason rows they track, and
// the count-column label (each supplied by its wrapper):
//   - Tiered     = TieredCustomerPerformance    (tiered / "New" customers):
//                  Uncovered Labor / Material Stockout / Quality / Shortage
//                  "issues" -> countLabel "Jobs with Issue".
//   - Non-Tiered = NonTieredCustomerPerformance (all customers):
//                  Customer Requested / AddOn "changes"
//                  -> countLabel "Jobs with Change".
// The response envelope is otherwise identical (same flat {week|qtd|ytd}JobCount
// totals and per-row {wtd,qtd,ytd} counts + reason/*FromDate/toDate), so the
// table structure, drill-down, and client-side percent computation are shared.
//
// `rows` is an array of { key, label, slug }: `key` selects the response object,
// `label` is the row heading, and `slug` ties the row to its drill-down route
// (/customer-performance/:reason). The drill-down (GetOrderChangeHistory) is not
// tier-filtered, so the same route serves both variants.
//
// dataScope === "self" scopes to the logged-in salesperson (order.SalesPersonId).

// Each period renders three sub-columns: <countLabel> | Total Jobs | %.
const PERIODS = [
  { id: "wtd", label: "Week to Date" },
  { id: "qtd", label: "Quarter to Date" },
  { id: "ytd", label: "Year to Date" },
];

const CustomerPerformanceWidget = ({
  auth: { auth },
  endpoint,
  title,
  rows = [],
  countLabel = "Jobs with Issue",
  dataScope = "all",
  userId,
}) => {
  const [data, setData] = useState(null);
  // Tracks which individual cell (e.g. "qualities:qtd") was last clicked so we
  // can highlight it and show the user exactly what they selected.
  const [selectedCell, setSelectedCell] = useState(null);
  const navigate = useNavigate();

  const token = auth && auth[0] ? auth[0].token : null;
  const salesPersonId = auth && auth[0] ? auth[0]?.salesPersonId : null;
  let tmpUserId = userId;
  if(auth && (auth[0]?.roleName ==='Sales' && salesPersonId !=='')){
    tmpUserId = salesPersonId
  }
  const scopedSalesPersonId = dataScope === "self" ? tmpUserId : null;


  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    const params = {};
    if (scopedSalesPersonId) params.salesPersonId = scopedSalesPersonId;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setData(rows[0] || null);
      })
      .catch((err) => {
        if (!active) return;
        console.warn(`Could not load ${title}:`, err.message);
      });
    return () => {
      active = false;
    };
  }, [token, scopedSalesPersonId, endpoint]);

  // Drill down to the listing, forwarding the row's reason + date window from
  // the widget response so GetOrderChangeHistory filters on the same values.
  // The from-date depends on which part of the row was clicked: the label and
  // the QTD cell use qtdFromDate (quarter-to-date), the YTD cell uses
  // ytdFromDate (year-to-date). Both share the same toDate.
  const handleRowClick = (r, period, cellId) => {
    setSelectedCell(cellId);
    const rowData = data && data[r.key] ? data[r.key] : null;
    const params = new URLSearchParams();
    if (rowData) {
      if (rowData.reason != null) params.set("reason", rowData.reason);
      const fromDate =
        period === "ytd"
          ? rowData.ytdFromDate
          : period === "wtd"
          ? rowData.weekFromDate
          : rowData.qtdFromDate;
      // Send only the date part (YYYY-MM-DD), not the full ISO datetime.
      if (fromDate) params.set("fromDate", toApiDate(fromDate));
      if (rowData.toDate) params.set("toDate", toApiDate(rowData.toDate));
    }
    params.set('filterType', (endpoint ==='TieredCustomerPerformance' ? 'Tiered' : 'NonTiered'))
    const qs = params.toString();
    navigate(`/customer-performance/${r.slug}${qs ? `?${qs}` : ""}`);
  };

  return (
    <div className="cpw-widget">
      <h4 className="cpw-title">{title}</h4>
      <table className="cpw-table">
        <thead>
          <tr className="cpw-group-row">
            <th className="cpw-group" rowSpan={2}></th>
            {PERIODS.map((p) => (
              <th key={p.id} className="cpw-group cpw-period-start" colSpan={3}>
                {p.label}
              </th>
            ))}
          </tr>
          <tr className="cpw-subhead-row">
            {PERIODS.map((p) => (
              <React.Fragment key={p.id}>
                <th className="cpw-group cpw-sub cpw-period-start">{countLabel}</th>
                <th className="cpw-sub">Total Jobs</th>
                <th className="cpw-sub">%</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const labelId = `${r.key}:label`;
            const cellCls = (base, id) =>
              `${base} cpw-cell${selectedCell === id ? " cpw-selected" : ""}`;
            return (
              <tr key={r.key}>
                <td
                  className={cellCls("cpw-label", labelId)}
                  onClick={() => handleRowClick(r, "ytd", labelId)}
                >
                  {r.label}
                </td>
                {PERIODS.map((p) => {
                  const issueId = `${r.key}:${p.id}`;
                  return (
                    <React.Fragment key={p.id}>
                      {/* Only the count cell drills down; Total Jobs and % are read-only. */}
                      <td
                        className={cellCls("cpw-num cpw-period-start", issueId)}
                        onClick={() => handleRowClick(r, p.id, issueId)}
                      >
                        {getCell(data, r.key, p.id)}
                      </td>
                      <td className="cpw-num cpw-muted">
                        {getTotalJobs(data, p.id)}
                      </td>
                      <td className="cpw-num cpw-muted">
                        {getIssuePercent(data, r.key, p.id)}%
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(CustomerPerformanceWidget);
